import { useRef, useState } from 'react';

import styles from './pagedTable.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchField from '../search-field/SearchField';
import { getHighlightedText, getSortIndex } from '../../features/visual-analytics/helpers/misc';
import { useColumnResize } from '../hooks/useColumnResize';

export interface TpPagedTableColumn {
  id: string;
  displayName: string;
  width: number;
  data: any[];
  convertData2String: (val: any) => string;
}

export interface TpPagedTableData {
  rowCount: number;
  columns: TpPagedTableColumn[];
}

export interface TpPagedTableProps {
  data: TpPagedTableData;
  defaultSortColId: string;
  defaultSortInv?: boolean;
  onRowClicked: (sorNr: number, rowData: any) => any;
}

function PagedTable({ data, defaultSortColId, defaultSortInv, onRowClicked }: TpPagedTableProps) {
  const refTableWrapper = useRef<HTMLDivElement>(null);

  const [searchString, setSearchString] = useState('');
  const [sortByColId, setSortByColId] = useState(defaultSortColId);
  const [sortInv, setSortInv] = useState(defaultSortInv || false);

  const pageRowCount = 20;
  const [pageOffset, setPageOffset] = useState(0);

  const { columnWidths, totalTableWidth, onPointerDownResize } = useColumnResize({
    initialColumns: data.columns.map((col) => ({ id: col.id, width: col.width })),
  });

  const columnMapper: { [colId: string]: TpPagedTableColumn } = {};
  for (const col of data.columns) columnMapper[col.id] = col;

  const sortIndex = getSortIndex(columnMapper[sortByColId].data, sortInv);

  function getRowData(rowNr: number): any {
    const rowData: any = {};
    for (const col of data.columns) {
      rowData[col.id] = col.data[rowNr];
    }
    return rowData;
  }

  const rows = [];
  const headers = data.columns.map((column: TpPagedTableColumn, index: number) => (
    <th key={column.id} style={{ width: `${columnWidths[index]}px` }}>
      <span className={styles.columnResizer} onPointerDown={(e) => onPointerDownResize(e, index)}></span>
      <div
        className={column.id == sortByColId ? styles.columnSortButtonActive : styles.columnSortButton}
        onClick={() => {
          if (column.id != sortByColId) {
            setSortByColId(column.id);
            setSortInv(false);
          } else {
            setSortInv(!sortInv);
          }
        }}
      >
        {column.id == sortByColId && sortInv ? (
          <FontAwesomeIcon icon="caret-down" />
        ) : (
          <FontAwesomeIcon icon="caret-up" />
        )}
      </div>
      <i title={column.displayName}>{column.displayName}</i>
    </th>
  ));

  let displayedCount = 0;
  let endOfPageOffset = 0;

  for (let rowNr = 0; rowNr < data.rowCount; rowNr++) {
    const rowNrSorted = sortIndex[rowNr];
    let inMatch = true;
    if (searchString) {
      inMatch = false;
      for (const column of data.columns) {
        const content = column.convertData2String(column.data[rowNrSorted]);
        if (content && String(content).toLowerCase().indexOf(searchString.toLowerCase()) >= 0) inMatch = true;
      }
    }
    if (inMatch) {
      if (displayedCount >= pageOffset && displayedCount < pageOffset + pageRowCount) {
        endOfPageOffset = displayedCount;
        rows.push(
          <tr
            key={`rw_${rowNrSorted}`}
            onClick={() => {
              onRowClicked(rowNrSorted, getRowData(rowNrSorted));
            }}
          >
            {data.columns.map((column, index) => (
              <td key={column.id}>
                {getHighlightedText(column.convertData2String(column.data[rowNrSorted]), searchString)}
              </td>
            ))}
          </tr>,
        );
      }
      displayedCount++;
    }
  }

  return (
    <div className={styles.fullWrapper}>
      <div className={styles.tableWrapper} ref={refTableWrapper}>
        <table style={{ width: `${totalTableWidth}px` }}>
          <thead>
            <tr>{headers}</tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <div style={{ display: 'inline-block', width: '140px' }}>
          <SearchField
            updateSearchString={(newVal: string) => {
              setSearchString(newVal);
              setPageOffset(0);
            }}
          />
        </div>
        <div className={styles.pager}>
          {pageOffset > 0 && (
            <button onClick={() => setPageOffset(Math.max(0, pageOffset - pageRowCount))}>
              <FontAwesomeIcon icon="caret-left" />
            </button>
          )}
          {pageOffset + 1} - {endOfPageOffset + 1} / {displayedCount}
          {endOfPageOffset + 1 < displayedCount && (
            <button onClick={() => setPageOffset(pageOffset + pageRowCount)}>
              <FontAwesomeIcon icon="caret-right" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PagedTable;
